import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Copy, Check, Sparkles, TicketPercent } from "lucide-react";
import { useLocation } from "react-router-dom";
import { subscribeEmail } from "../lib/shop";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const SESSION_KEY = "01l_nl_shown";
const DONE_KEY = "01l_nl_done";
const CHECKOUT_KEY = "01l_checkout_at";
const TAB_KEY = "01l_offer_tab_dismissed";

export default function NewsletterModal() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onProductPage = location.pathname.startsWith("/product/");
  const { isOpen: cartOpen } = useCart();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("WELCOME10");
  const busy = useRef(false);
  const locationRef = useRef(location.pathname);
  const cartOpenRef = useRef(cartOpen);
  const [offerTabHidden, setOfferTabHidden] = useState(
    () => sessionStorage.getItem(TAB_KEY) === "1" || !!localStorage.getItem(DONE_KEY)
  );
  const [footerVisible, setFooterVisible] = useState(false);
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [tabCovered, setTabCovered] = useState(true);

  // the offer tab never covers footer links or the hero quick-add CTA —
  // it steps aside while either is on screen (re-checked on every route change)
  useEffect(() => {
    const f = document.querySelector("footer");
    const card = document.querySelector('[data-testid="hero-quick-add-card"]');
    setFooterVisible(false);
    setQuickAddVisible(false);
    if (!f && !card) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === f) setFooterVisible(e.isIntersecting);
        if (e.target === card) setQuickAddVisible(e.isIntersecting);
      }
    }, { threshold: 0.1 });
    if (f) obs.observe(f);
    if (card) obs.observe(card);
    return () => obs.disconnect();
  }, [location.pathname]);

  // the tab never floats over content: while scrolling it hides, and it only
  // reappears when its corner is genuinely clear of links, buttons, images and text
  useEffect(() => {
    let debounce = null;
    const check = () => {
      const tab = document.querySelector('[data-testid="offer-tab"]');
      if (!tab) return;
      const tr = tab.getBoundingClientRect();
      if (tr.width === 0) return;
      let covered = false;
      const els = document.querySelectorAll("a, button, img, h1, h2, h3, h4, p, span, li");
      for (const el of els) {
        if (tab.contains(el)) continue;
        const r = el.getBoundingClientRect();
        if (r.width < 8 || r.height < 8) continue;
        if (r.left >= tr.right || r.right <= tr.left || r.top >= tr.bottom || r.bottom <= tr.top) continue;
        covered = true;
        break;
      }
      setTabCovered(covered);
    };
    const schedule = () => {
      clearTimeout(debounce);
      debounce = setTimeout(check, 300);
    };
    // safety net: geometry can settle after the debounced check (smooth-scroll
    // tails, late reveals) — a slow interval guarantees convergence
    const iv = setInterval(check, 1200);
    const t = setTimeout(check, 500);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      clearTimeout(t);
      clearTimeout(debounce);
      clearInterval(iv);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [location.pathname]);

  const openOffer = () => {
    // manual open — also stops the auto-fire for the rest of the session
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(true);
  };

  const dismissOfferTab = () => {
    // remembered for the current session only
    sessionStorage.setItem(TAB_KEY, "1");
    setOfferTabHidden(true);
  };

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    cartOpenRef.current = cartOpen;
  }, [cartOpen]);

  useEffect(() => {
    let retryTimer = null;
    let attempts = 0;
    let scrollArmed = true;

    // auto-fire rules: never on mobile product pages (offer stays reachable via the
    // 10% OFF button), never while the cart drawer is open, never within 2 minutes of
    // a checkout handoff — otherwise delayed until the customer has had time to browse
    const check = () => {
      if (sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(DONE_KEY)) return "done";
      if (locationRef.current.startsWith("/product/") && window.matchMedia("(max-width: 1023px)").matches) return "mobile-pdp";
      if (cartOpenRef.current) return "cart-open";
      const at = parseInt(localStorage.getItem(CHECKOUT_KEY) || "0", 10);
      if (at && Date.now() - at < 120000) return "checkout-active";
      return "ok";
    };

    const show = () => {
      const status = check();
      if (status === "done") return;
      if (status !== "ok") {
        // transient blocks (cart open / checkout just happened) retry gently, capped
        if (status !== "mobile-pdp" && attempts < 4 && !retryTimer) {
          attempts += 1;
          retryTimer = setTimeout(show, 30000);
        }
        return;
      }
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
      cleanup();
    };

    const t = setTimeout(show, 45000);
    const onLeave = (e) => {
      if (e.clientY <= 0) show();
    };
    const onScroll = () => {
      if (scrollArmed && (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight > 0.55) {
        scrollArmed = false;
        show();
      }
    };
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    const onOpen = () => {
      clearTimeout(t);
      if (retryTimer) clearTimeout(retryTimer);
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    };
    window.addEventListener("open-newsletter", onOpen);
    function cleanup() {
      clearTimeout(t);
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onScroll);
    }
    return cleanup;
  }, []);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setError("");
    setState("loading");
    try {
      const res = await subscribeEmail(email);
      setCode(res.code);
      setState("done");
      localStorage.setItem(DONE_KEY, "1");
      localStorage.setItem("01l_email", email);
      setOfferTabHidden(true);
    } catch (err) {
      setError(err.message || "Something went wrong");
      setState("idle");
    }
    busy.current = false;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copied — apply it at checkout");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — write it down: " + code);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-5" data-testid="email-capture-modal">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
              onClick={close}
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-coal border border-white/10 w-full max-w-md overflow-hidden"
            >
              <div className="h-1 bg-ember" />
              <button
                onClick={close}
                className="absolute top-4 right-4 p-2 text-ash hover:text-bone transition-colors"
                aria-label="Close"
                data-testid="email-capture-close-button"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>

              <div className="p-8 md:p-10">
                {state !== "done" ? (
                  <>
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-ember uppercase mb-5">
                      <Sparkles className="w-3.5 h-3.5" /> First order only
                    </div>
                    <h3 className="font-display font-extrabold uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
                      GET 10% OFF
                      <br />
                      YOUR FIRST ORDER
                    </h3>
                    <p className="text-ash text-sm mt-4 leading-relaxed">
                      Join the list — instant code, no noise. Heavyweight drops, early access, nothing else.
                    </p>
                    <form onSubmit={submit} className="mt-7 space-y-3">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full bg-ink border border-white/15 focus:border-bone outline-none px-4 py-3.5 font-mono text-sm placeholder:text-ash/60 transition-colors"
                        data-testid="email-capture-input"
                      />
                      {error && <p className="text-ember font-mono text-[11px]" data-testid="email-capture-error">{error}</p>}
                      <button
                        type="submit"
                        disabled={state === "loading"}
                        className="w-full bg-bone text-ink font-mono text-xs tracking-[0.22em] py-4 hover:bg-ember hover:text-white transition-colors duration-300 disabled:opacity-50"
                        data-testid="email-capture-submit-button"
                      >
                        {state === "loading" ? "GETTING YOUR CODE…" : "UNLOCK 10% OFF"}
                      </button>
                      <p className="font-mono text-[9px] tracking-[0.08em] text-ash uppercase leading-relaxed">
                        By signing up you agree to receive marketing emails. 10% off your first order. See our <Link to="/policies/privacy-policy" onClick={close} className="text-bone underline underline-offset-2 hover:text-ember">privacy policy</Link>.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-2" data-testid="email-capture-success">
                    <div className="w-12 h-12 mx-auto bg-ember/15 border border-ember/40 flex items-center justify-center mb-6">
                      <TicketPercent className="w-5 h-5 text-ember" />
                    </div>
                    <h3 className="font-display font-extrabold uppercase tracking-tighter text-3xl">YOU'RE IN.</h3>
                    <p className="text-ash text-sm mt-3">
                      Use this code at checkout for 10% off your first order — we've also emailed it to{" "}
                      <span className="text-bone">{email}</span>:
                    </p>
                    <button
                      onClick={copy}
                      className="mt-6 w-full border border-dashed border-bone/50 hover:border-ember hover:bg-ember/10 py-4 flex items-center justify-center gap-3 font-mono text-xl font-bold tracking-[0.3em] transition-colors"
                      data-testid="discount-code-copy-button"
                    >
                      {code}
                      {copied ? <Check className="w-4 h-4 text-ember" /> : <Copy className="w-4 h-4 text-ash" />}
                    </button>
                    <p className="font-mono text-[9px] tracking-[0.14em] text-ash uppercase mt-4">Tap to copy</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {!offerTabHidden && (
        <div
          className={`fixed z-[70] right-3 flex items-center bg-ember text-white shadow-2xl transition-opacity duration-300 ${footerVisible || quickAddVisible || tabCovered ? "opacity-0 pointer-events-none" : "opacity-100"} ${onProductPage ? "bottom-24 lg:bottom-5 lg:right-5" : "bottom-3 lg:bottom-5 lg:right-5"}`}
          data-testid="offer-tab"
        >
          <button
            onClick={openOffer}
            className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.15em] px-3 py-2.5"
            data-testid="email-capture-trigger"
          >
            <TicketPercent className="w-3.5 h-3.5" />
            10% OFF
          </button>
          <button
            onClick={dismissOfferTab}
            aria-label="Dismiss offer for this session"
            className="p-1.5 pr-2 opacity-70 hover:opacity-100 transition-opacity"
            data-testid="offer-tab-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </>
  );
}
